import { CheckboxGroup, CheckboxOption } from '@distribusion/owl-frontend-ui'
import { addHours, endOfDay, isWithinInterval, startOfDay, clamp } from 'date-fns'
import React, { FC, useMemo } from 'react'

import { FilterConfig, FilterFunction, FilterRenderProps } from '@lib/connections/filters/types'
import dateUtils from '@lib/date'
import { useTranslation } from '@lib/i18n'
import { withPluginSlot } from '@plugins/index'
import { DayInterval } from '@stores/connectionFilters'

interface TimeInterval {
  start: Date
  end: Date
}

export const DAYTIME_INTERVAL_NAMES = ['dawn', 'morning', 'afternoon', 'night'] as const
const DAYTIME_INTERVALS = {
  dawn: { start: 0, end: 6 },
  morning: { start: 6, end: 12 },
  afternoon: { start: 12, end: 18 },
  night: { start: 18, end: 24 },
}

const buildDateRange = (baseDate: Date, intervalName: DayInterval): TimeInterval => {
  const day = { start: startOfDay(baseDate), end: endOfDay(baseDate) }
  const interval = DAYTIME_INTERVALS[intervalName]
  return { start: addHours(day.start, interval.start), end: clamp(addHours(day.start, interval.end), day) }
}

export const checkIsCorrectDaytime = (date: Date, daytime: DayInterval): boolean => {
  return isWithinInterval(date, buildDateRange(date, daytime))
}

const filter: FilterFunction = (connection, value) => {
  const departureTime = dateUtils.parse(connection.departureTime, 'UTC')

  return value.some(interval => checkIsCorrectDaytime(departureTime, interval as DayInterval))
}

interface LabelProps {
  daytime: DayInterval
}

const Label: FC<LabelProps> = withPluginSlot(({ daytime }) => {
  const { t } = useTranslation()
  const interval = DAYTIME_INTERVALS[daytime]
  const start = `${interval.start.toString().padStart(2, '0')}:00`
  const end = `${interval.end.toString().padStart(2, '0')}:00`

  return <span>{`${t(`journeyList.filters.${daytime}`)} (${start} - ${end})`}</span>
}, 'departureTimeFilter.optionLabel')

const FilterComponent = ({ value, onChange }: FilterRenderProps<DayInterval[]>) => {
  const options: CheckboxOption<DayInterval>[] = useMemo(
    () => DAYTIME_INTERVAL_NAMES.map(daytime => ({ value: daytime, label: <Label daytime={daytime} /> })),
    [],
  )

  return <CheckboxGroup value={value} onChange={onChange} options={options} />
}

const departureTimeFilter: FilterConfig<DayInterval[]> = {
  filter,
  title: 'departureTime',
  Component: FilterComponent,
}

export default departureTimeFilter
